"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import PricingCard from "../pricing/PricingCard";
import type { PricingType } from "../pricing/pricingSection.types";
import type { AdaptedFee } from "@/helpers/pricing/adapters/adaptToFee";
import { ArrowRightCircleWrapped } from "../icons/arrow-right-circle-wrapped";
import { ArrowLeftCircleWrapped } from "../icons/arrow-left-circle-wrapped";

type MobilePricingCard = {
  data: PricingType;
  fees: AdaptedFee[];
  guideAnchorId: string;
};

export default function MobilePricingCarousel({ cards }: { cards: MobilePricingCard[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const scrollToIndex = useCallback(
    (index: number) => {
      const container = containerRef.current;
      if (!container) return;
      const clampedIndex = Math.max(0, Math.min(cards.length - 1, index));
      container.scrollTo({
        left: clampedIndex * container.clientWidth,
        behavior: "smooth",
      });
      setActiveIndex(clampedIndex);
    },
    [cards.length],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let rafId = 0;
    const handleScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        const nextIndex = Math.round(container.scrollLeft / container.clientWidth);
        setActiveIndex(Math.max(0, Math.min(cards.length - 1, nextIndex)));
      });
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      container.removeEventListener("scroll", handleScroll);
    };
  }, [cards.length]);

  if (!cards.length) return null;

  return (
    <div className="relative w-full">
      <div
        ref={containerRef}
        className="flex w-full overflow-x-auto scroll-smooth snap-x snap-mandatory"
      >
        {cards.map((card, index) => (
          <div key={index} className="w-full shrink-0 snap-center box-border px-1">
            <PricingCard data={card.data} fees={card.fees} guideAnchorId={card.guideAnchorId} />
          </div>
        ))}
      </div>

      {activeIndex > 0 && (
        <button
          type="button"
          onClick={() => scrollToIndex(activeIndex - 1)}
          aria-label="Previous pricing card"
          className="absolute left-1 top-[47%] -translate-y-[47%] animate-pulse"
        >
          <ArrowLeftCircleWrapped width={58} height={58} />
        </button>
      )}

      {activeIndex < cards.length - 1 && (
        <button
          type="button"
          onClick={() => scrollToIndex(activeIndex + 1)}
          aria-label="Next pricing card"
          className="absolute right-1 top-[47%] -translate-y-[47%] animate-pulse"
        >
          <ArrowRightCircleWrapped width={58} height={58} />
        </button>
      )}
    </div>
  );
}
